import type { HyperFramesComposition } from './types'

function safeString(value: any) {
  return String(value || '')
}

export function buildHyperFramesSrcDoc(
  composition: HyperFramesComposition,
  options?: {
    controlled?: boolean
  }
) {
  const html = safeString(composition?.html)
  const css = safeString(composition?.css)
  const js = safeString(composition?.js)

  const width = Number(composition?.width || 1080)
  const height = Number(composition?.height || 1920)
  const controlled = Boolean(options?.controlled)

  if (!html && !css && !js) return ''

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta
    name="viewport"
    content="width=device-width, initial-scale=1"
  />
  <script src="https://cdn.jsdelivr.net/npm/gsap@3.14.2/dist/gsap.min.js"></script>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    html,
    body {
      margin: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
      background: transparent !important;      
    }

    body {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    #hf-preview-stage {
      position: relative;
      width: 100vw;
      height: 100vh;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent !important;
    }

    #hf-preview-scale {
        position: relative;
        width: ${width}px;
        height: ${height}px;
        transform-origin: center center;
    }

    ${css}
    html,
    body,
    #hf-preview-stage,
    #hf-preview-scale,
    #root {
    background: transparent !important;
    }
  </style>
</head>
<body>
  <div id="hf-preview-stage">
    <div id="hf-preview-scale">
      ${html}
    </div>
  </div>

  <script>
    window.__timelines = window.__timelines || {};

    ${js}

    function resizePreview() {
        const stage = document.getElementById("hf-preview-stage");
        const scaleEl = document.getElementById("hf-preview-scale");

        if (!stage || !scaleEl) return;

        const stageWidth = stage.clientWidth || window.innerWidth;
        const stageHeight = stage.clientHeight || window.innerHeight;

        const compositionWidth = ${width};
        const compositionHeight = ${height};

        const scale = Math.min(
            stageWidth / compositionWidth,
            stageHeight / compositionHeight
        );

        scaleEl.style.transform = "scale(" + scale + ")";
    }

    window.addEventListener("resize", resizePreview);
    resizePreview();

    ${controlled ? `
      // Controlled timeline mode:
      // Used by VideoEditorTab so the parent player controls GSAP timecode.
      function getHyperframesTimelines() {
        const timelines = window.__timelines || {};
        return Object.keys(timelines)
          .map((id) => timelines[id])
          .filter(Boolean);
      }

      function seekHyperframes(timeSec, playing) {
        const timelines = getHyperframesTimelines();

        timelines.forEach((tl) => {
          if (!tl) return;

          try {
            const duration =
              typeof tl.duration === "function"
                ? Math.max(0.001, tl.duration())
                : 0.001;

            const safeTime = Math.max(
              0,
              Math.min(Number(timeSec || 0), duration)
            );

            if (typeof tl.pause === "function") {
              tl.pause();
            }

            if (typeof tl.time === "function") {
              tl.time(safeTime, false);
            }

            if (playing && typeof tl.play === "function") {
              tl.play();
            }
          } catch (error) {
            console.error("HyperFrames controlled seek error", error);
          }
        });
      }

      window.addEventListener("message", function (event) {
        const data = event.data || {};

        if (data.type !== "HYPERFRAMES_SEEK") return;

        seekHyperframes(data.timeSec, data.playing);
      });

      setTimeout(() => {
        seekHyperframes(0, false);
      }, 100);
  ` : `
      // Autoplay preview mode:
      // Used by SceneDetailView so the composition previews by itself.
      setTimeout(() => {
        const timelines = window.__timelines || {};
        Object.keys(timelines).forEach((id) => {
          const tl = timelines[id];

          if (!tl || typeof tl.restart !== "function") return;

          try {
            tl.eventCallback("onComplete", () => {
              setTimeout(() => {
                tl.restart(true);
              }, 500);
            });

            tl.restart(true);
          } catch (error) {
            console.error("HyperFrames preview playback error", error);
          }
        });
      }, 100);
  `}
  </script>
</body>
</html>`
}